import React from 'react';
import { isIncreasedFlexibilityEnabled } from '@helpcenter/feature-flags';
import { di } from 'react-magnetic-di';
import { useIsPageInEditMode, useIsPageInPreviewMode } from 'view/layout-next/controllers';
import { Box, xcss } from '@atlaskit/primitives';
import type { XCSS } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';

// eslint-disable-next-line no-shadow
export enum ElementWidth {
    FULL_WIDTH = 'full-width',
    COMPACT = 'compact',
}

// eslint-disable-next-line no-shadow
export enum ElementMargins {
    STANDARD = 'standard',
    COMPACT = 'compact',
}

interface ElementRootProps {
    elementRef?: React.RefObject<HTMLElement>;
    isCustomizing: boolean;
    elementWidth?: ElementWidth; // default is full-width
    elementMargins?: ElementMargins; // default is standard
    removePadding?: boolean;
    removeMargin?: boolean;
    customStyles?: XCSS[];
    hasTopBarBeenSplit?: boolean;
}

export const ElementRoot: React.FC<React.PropsWithChildren<ElementRootProps>> = ({
    elementRef,
    children,
    isCustomizing,
    elementWidth,
    elementMargins,
    removePadding, // deprecated after enabling flag isIncreasedFlexibilityEnabled
    removeMargin, // for use-case of forge where we give margin internally
    customStyles,
    hasTopBarBeenSplit,
}) => {
    di(isIncreasedFlexibilityEnabled);

    const [isPageInEditMode] = useIsPageInEditMode();
    const [isPageInPreviewMode] = useIsPageInPreviewMode();

    const isIncreasedFlexibilityEnabledWithTopBarSplit = isIncreasedFlexibilityEnabled() && hasTopBarBeenSplit;

    const rootXCSS = React.useMemo(() => {
        const xcssStyle = [isIncreasedFlexibilityEnabledWithTopBarSplit ? rootStyleIncreasedFlexibility : rootStyle];
        if (elementWidth === ElementWidth.COMPACT) {
            if (isCustomizing) {
                xcssStyle.push(rootStyleWithSidebarOpened);
            } else {
                xcssStyle.push(rootStyleWithSidebarClosed);
            }
        } else if (isIncreasedFlexibilityEnabledWithTopBarSplit && elementWidth === ElementWidth.FULL_WIDTH) {
            xcssStyle.push(rootStyleFullWith);
        }
        if (!removePadding && !isIncreasedFlexibilityEnabledWithTopBarSplit) {
            xcssStyle.push(rootPaddingStyle);
        }
        if (isIncreasedFlexibilityEnabledWithTopBarSplit && !removeMargin) {
            if (!isPageInEditMode || isPageInPreviewMode) {
                // only don't apply while page edit
                if (elementMargins === ElementMargins.COMPACT) {
                    xcssStyle.push(rootMarginStyleCompact);
                } else {
                    xcssStyle.push(rootMarginStyleStandard);
                }
            }
        }

        if (customStyles) {
            return [...xcssStyle, ...customStyles];
        }
        return xcssStyle;
    }, [
        isIncreasedFlexibilityEnabledWithTopBarSplit,
        elementWidth,
        removePadding,
        removeMargin,
        customStyles,
        isCustomizing,
        isPageInEditMode,
        isPageInPreviewMode,
        elementMargins,
    ]);
    return (
        <Box ref={elementRef} testId={'element-root'} xcss={rootXCSS}>
            {children}
        </Box>
    );
};

const rootStyle = xcss({
    alignSelf: 'center',
    width: '100%',
    alignItems: 'center',
});

const rootStyleIncreasedFlexibility = xcss({
    alignSelf: 'center',
    width: '100%',
    alignItems: 'center',
    // do not add margin and padding is there is no children
    ':empty': {
        display: 'none',
    },
});

const rootStyleFullWith = xcss({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
    width: 'auto',
});

const rootPaddingStyle = xcss({
    paddingTop: 'space.300',
    paddingBottom: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles,rulesdir/no-styled-export
export const rootMarginStyleCompact = xcss({
    marginLeft: 'space.200',
    marginRight: 'space.200',
    marginTop: 'space.200',
    marginBottom: 'space.200',
    [media.above.xs]: {
        marginLeft: 'space.400',
        marginRight: 'space.400',
    },
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles,rulesdir/no-styled-export
export const rootMarginStyleStandard = xcss({
    marginLeft: 'space.400',
    marginRight: 'space.400',
    marginTop: 'space.400',
    marginBottom: 'space.400',
});

const rootStyleWithSidebarOpened = xcss({
    maxWidth: '336px', // 42 * 8
    // @ts-ignore // because use using custom breakpoints
    '@media (min-width: 80rem)': {
        // ((110 + SIDEBAR_WIDTH_LARGE_MULTIPLIER) * 8) / 16;
        maxWidth: '720px', // 90 * 8
    },
    '@media (min-width: 102rem)': {
        // ((155 + SIDEBAR_WIDTH_LARGE_MULTIPLIER) * 8) / 16;
        maxWidth: '1160px', // 145 * 8
    },
});

const rootStyleWithSidebarClosed = xcss({
    maxWidth: '336px', // 42 * 8
    // @ts-ignore // because use using custom breakpoints
    '@media (min-width: 60rem)': {
        maxWidth: '720px', // 90 * 8
    },
    '@media (min-width: 80rem)': {
        maxWidth: '1160px', // 145 * 8
    },
});
